import { SupabaseClient } from "@supabase/supabase-js";
import { PaymentTier } from "@/app/types/payment";
import { Order, Product, ProductWithTaskStatus, TaskStatus } from "@/app/types/products";
import WorkFactory from "../workFactory";
import * as ProductsRepository from './ProductsRepository';

/**
 * Get active products for a specific payment tier
 *
 * @param client - Supabase client
 * @param paymentTier - The payment tier to filter by
 * @returns The product for the specified tier or null if not found
 */
export async function getActiveProductByPaymentTier(
  client: SupabaseClient,
  paymentTier: PaymentTier
): Promise<Product | null> {
  const { data, error } = await client
    .from("products")
    .select("*")
    .eq("code_name", paymentTier)
    .eq("product_type", "tier")
    .eq("state", "active")
    .maybeSingle();

  if (error) {
    throw new Error(`Error getting product by payment tier: ${error.message}`);
  }

  if (!data) {
    return null;
  }

  return WorkFactory.productDataToProductObject(data);
}

/**
 * Fetch all products that are Tiers
 * 
 * @param client - Supabase client
 * @returns Array of product objects
 */
export async function getTierProducts(client: SupabaseClient): Promise<Product[] | null> {
  const { data, error } = await client
    .from("products")
    .select("*")
    .eq("state", "active")
    .eq("product_type", "tier")
    .order("name", { ascending: true });

  if (error) {
    throw new Error(`Getting products: ${error.message}`);
  }

  if (!data) {
    return [];
  }

  return (data || []).map((product: any) => WorkFactory.productDataToProductObject(product));
}

/**
 * Fetch all active products sorted alphabetically.
 *
 * @param client - Supabase client
 * @returns Array of active product objects
 */
export async function getActiveProducts(client: SupabaseClient): Promise<Product[] | null> {
  const { data, error } = await client
    .from("products")
    .select("*")
    .eq("state", "active")
    .order("name", { ascending: true });

  if (error) {
    throw new Error(`Getting products: ${error.message}`);
  }

  if (!data) {
    return [];
  }

  return (data || []).map((product: any) => WorkFactory.productDataToProductObject(product));
}

/**
 * Return the products that the for a specific work along with any background task status.
 *
 * @param client - Supabase client
 * @param workId - Work the products are associated with
 * @param productIds - Product IDs to load
 * @param backgroundTasks - Optional tasks to merge with the products
 * @returns Array of products with their background task status
 */
export async function getUserProducts(
  client: SupabaseClient,
  workId: string,
  productIds: string[],
  backgroundTasks: TaskStatus[] | null
): Promise<ProductWithTaskStatus[]> {
  const { data, error } = await client
    .from("products")
    .select("*")
    .eq("state", "active")
    .in("id", productIds)
    .order("name", { ascending: true });

  if (error) {
    throw new Error(`Error getting user products: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return [];
  }

  return data.map((product: any) => {
    const baseProduct = WorkFactory.productDataToProductObject(product);
    const matchingTask = backgroundTasks?.find(
      (task) => task.product_code_name === product.code_name
    ) as TaskStatus | undefined;

    if (matchingTask?.task_metadata && typeof matchingTask.task_metadata === "string") {
      try {
        matchingTask.task_metadata = JSON.parse(matchingTask.task_metadata);
      } catch (error) {
        console.warn(`Error parsing task metadata: ${error}`);
        matchingTask.task_metadata = {} as any;
      }
    }

    const productWithStatus: ProductWithTaskStatus = {
      ...baseProduct,
      taskStatus: matchingTask || undefined,
    };

    return productWithStatus;
  });
}

/**
 * Look up a single product by its unique identifier.
 *
 * @param client - Supabase client
 * @param productId - UUID of the product
 * @returns The product or null if it doesn't exist
 */
export async function getProductById(
  client: SupabaseClient,
  productId: string
): Promise<Product | null> {
  const { data, error } = await client
    .from("products")
    .select("*")
    .eq("id", productId)
    .limit(1);

  if (error) {
    throw new Error(`Error getting product: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return null;
  }

  return WorkFactory.productDataToProductObject(data[0]);
}

/**
 * Retrieve a product by its code name.
 *
 * @param client - Supabase client
 * @param productCodeName - Short code used to identify the product
 * @returns The product or null if not found
 */
export async function getProductByCodeName(
  client: SupabaseClient,
  productCodeName: string
): Promise<Product | null> {
  const { data, error } = await client
    .from("products")
    .select("*")
    .eq("code_name", productCodeName)
    .limit(1);

  if (error) {
    throw new Error(`Error getting product: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return null;
  }

  return WorkFactory.productDataToProductObject(data[0]);
}

/**
 * Create an order for a product or attach the product to an existing order.
 *
 * @param client - Supabase client
 * @param userId - Owner of the order
 * @param productId - Product being purchased
 * @param productPrice - Price in currency units
 * @param orderId - Optional existing order ID
 * @returns Newly created or existing order item record
 */
export async function purchaseProduct(
  client: SupabaseClient,
  userId: string,
  productId: string,
  productPrice: number,
  orderId?: string
): Promise<any> {
  let currentOrderId = orderId;

  if (!currentOrderId) {
    const { data: newOrder, error: createOrderError } = await client
      .from("orders")
      .insert({
        user_id: userId,
        stripe_payment_id: "00000000-0000-0000-0000-000000000000",
        status: "paid", // pending, paid, failed
        total_price: productPrice,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select("id")
      .single();

    if (createOrderError) {
      throw new Error(`Error creating order: ${createOrderError.message}`);
    }

    currentOrderId = newOrder.id;
  }

  const { data: existingItems, error: itemsError } = await client
    .from("order_items")
    .select("id")
    .eq("order_id", currentOrderId)
    .eq("product_id", productId)
    .limit(1);

  if (itemsError) {
    throw new Error(`Error checking existing order items: ${itemsError.message}`);
  }

  if (!existingItems || existingItems.length === 0) {
    const { data: newItem, error: createItemError } = await client
      .from("order_items")
      .insert({
        order_id: currentOrderId,
        product_id: productId,
        quantity: 1,
        price: productPrice,
        tax_amount: 0,
        created_at: new Date().toISOString(),
      })
      .select("*")
      .single();

    if (createItemError) {
      throw new Error(`Error creating order item: ${createItemError.message}`);
    }

    return newItem;
  }

  return existingItems[0];
}

/**
 * From a list of active products, return the product matching the given payment tier.
 *
 * @param activeProducts - Previously loaded active products
 * @param paymentTier - Desired payment tier
 * @returns Matching product or null if none
 */
export async function getProductTier(
  activeProducts: Product[] | null,
  paymentTier: PaymentTier
): Promise<Product | null> {
  if (!activeProducts) {
    return null;
  }
  const tieredProduct = activeProducts.find((product) => product.code_name === paymentTier);
  return tieredProduct || null;
}

/**
 * From a list of products, return those that belong to the given payment tier.
 *
 * @param activeProducts - Array of available products
 * @param paymentTier - Tier to filter by
 * @returns All products matching the tier or null when none available
 */
export async function getProductsByPaymentTier(
  activeProducts: Product[] | null,
  paymentTier: PaymentTier
): Promise<Product[] | null> {
  if (!activeProducts) {
    return null;
  }

  /*
    Some products (e.g. "Query Letter") do not have a numeric `paymentTier` in
    their metadata.  Instead, they are referenced from the parent tier product
    (tier0 / tier1 / tier2) via the `metadata.products` array.  We therefore
    need to include both:
      1. Products whose `metadata.paymentTier` contains the numeric tier
      2. Products whose code_name is referenced by the parent tier product
  */

  // Map the enum to its numeric tier(s)
  const numericTiers: number[] = [];
  if (paymentTier === PaymentTier.Tier0) numericTiers.push(0);
  if (paymentTier === PaymentTier.Tier1) numericTiers.push(1);
  if (paymentTier === PaymentTier.Tier2) numericTiers.push(2);

  // Find the tier product (e.g. code_name === "tier2") so we can read the
  // list of product code names explicitly included in that tier
  const tierProduct = activeProducts.find((p) => p.code_name === paymentTier);
  const productsExplicitlyInTier: Set<string> = new Set(
    (tierProduct?.metadata?.products as string[] | undefined) ?? []
  );

  // Build the result, avoiding duplicates
  const productsInPaymentTier: Product[] = [];
  const seen: Set<string> = new Set();

  for (const product of activeProducts) {
    const codeName = product.code_name;

    const hasNumericTier = (product.metadata?.paymentTier || []).some((t: number) =>
      numericTiers.includes(t)
    );
    const isExplicitlyListed = productsExplicitlyInTier.has(codeName);

    if ((hasNumericTier || isExplicitlyListed) && !seen.has(codeName)) {
      productsInPaymentTier.push(product);
      seen.add(codeName);
    }
  }

  return productsInPaymentTier;
}

/**
 * Fetch a single order by its identifier.
 *
 * @param client - Supabase client
 * @param orderId - UUID of the order to fetch
 * @returns The order record or null if it doesn't exist
 */
export async function getOrderById(client: SupabaseClient, orderId: string): Promise<Order | null> {
  const { data, error } = await client
    .from("orders")
    .select("*")
    .eq("id", orderId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data;
}


/**
 * Fetch all orders for a specific work.
 *
 * @param client - Supabase client
 * @param workId - UUID of the work to fetch orders for
 * @returns Array of order records or null if none exist
 */
export async function getOrdersForWork(client: SupabaseClient, workId: string): Promise<Order[] | null> {
  const { data, error } = await client
    .from("orders")
    .select("*")
    .eq("work_id", workId);

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Fetch the most recent paid order for the user that is not
 * associated with a work. Returns null when none exist.
 *
 * @param client - Supabase client
 * @param userId - ID of the user
 * @returns The unassociated order or null
 */
export async function getUnassociatedOrderForUser(
  client: SupabaseClient,
  userId: string,
): Promise<Order | null> {
  const { data, error } = await client
    .from("orders")
    .select("*")
    .eq("user_id", userId)
    .eq("status", "paid")
    .is("work_id", null)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data;
}

export async function getUnassociatedOrderWithProductForUser(
  client: SupabaseClient,
  userId: string,
): Promise<{ order: Order; product: Product } | null> {
  const { data, error } = await client
    .from('orders')
    .select('*, products:product_id(*)')
    .eq('user_id', userId)
    .eq('status', 'paid')
    .is('work_id', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return null;
  }

  const { products, ...order } = data as any;
  return { order: order as Order, product: products as Product };
}

/**
 * Returns the list of products the user has purchased for this work
 * @param client - Supabase client
 * @param workFriendlyId - Friendly ID of the work
 * @returns Array of products with their task status
 */
export async function getUserProductsForWork(
  client: SupabaseClient,
  workFriendlyId: string,
): Promise<ProductWithTaskStatus[]> {
  // Get the work by friendly ID
  const { data: workData, error: workError } = await client
    .from("works")
    .select("*")
    .eq("friendly_id", workFriendlyId)
    .single();

  if (workError) {
    throw new Error(`Work not found for friendly_id: ${workFriendlyId}`);
  }

  if (!workData) {
    throw new Error(`Work not found for friendly_id: ${workFriendlyId}`);
  }

  const work = workData;

  // Get orders for this work
  // We don't actually care about checking for the user here
  // since we don't care who paid for the work
  const { data: orders, error: ordersError } = await client
    .from("orders")
    .select("id")
    .eq("work_id", work.id);

  if (ordersError) {
    throw new Error(`Error getting orders: ${ordersError.message}`);
  }

  if (!orders || orders.length === 0) {
    return [];
  }

  // Get order items for these orders
  const orderIds = orders.map((order) => order.id);
  const { data: orderItems, error: orderItemsError } = await client
    .from("order_items")
    .select("product_id")
    .in("order_id", orderIds);

  if (orderItemsError) {
    throw new Error(`Error getting order items: ${orderItemsError.message}`);
  }

  if (!orderItems || orderItems.length === 0) {
    return [];
  }

  // Get background tasks for this work
  const { data: backgroundTasks, error: backgroundTasksError } = await client.rpc(
    'get_tasks_for_work_id',
    { p_work_id: work.id }
  );

  if (backgroundTasksError) {
    console.error(`Error getting background tasks: ${backgroundTasksError.message}`);
    throw new Error(`Error getting background tasks: ${backgroundTasksError.message}`);
  }

  // Get product tiers for this work
  const { data: productTiers, error: productTiersError } = await client
    .from('products')
    .select(
      `
    *,
    orders!inner(
      id
    )
  `
    )
    .eq('state', 'active')
    .eq('orders.work_id', work.id)
    .eq('product_type', 'tier')
    .order('name', { ascending: true });

  if (productTiersError) {
    console.error(`Error getting product tiers: ${productTiersError.message}`);
    throw new Error(`Error getting product tiers: ${productTiersError.message}`);
  }

  // Combine product IDs from order items and tiers
  const productIds = orderItems.map((item) => item.product_id);
  if (productTiers && productTiers.length > 0) {
    productTiers.forEach((tier) => {
      productIds.push(tier.id);
    });
  }

  // Get the actual products
  let products = await ProductsRepository.getUserProducts(
    client,
    work.id,
    productIds,
    backgroundTasks as any
  );

  // Filter out superseded products
  if (products && products.length > 0) {
    const { filterSupersededProducts } = await import("../../productUtils");
    products = filterSupersededProducts(products);
  }

  return products;
}
